/**
 * MakeItYoursWizard Component
 * 2-step wizard for quick customization of predefined templates
 * Steps: 1. Topic & Audience  2. AI-Enhanced Scenes
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Modal, Input, Tooltip, Button, message, Select } from 'antd';
import { useAtom, useSetAtom } from 'jotai';
import { Sparkles, Users, Clock, Layout, Loader2, MessageSquare, Target, Mic, RefreshCw, ChevronRight, ChevronLeft, Check, Lock } from 'lucide-react';
import { WizardProgress } from './WizardProgress';
import { WizardNavigation } from './WizardNavigation';
import { makeItYoursStateAtom, closeWizardAtom, CustomizedScene, SceneAIStatus, ContentTone } from '../../../atoms/wizardAtoms';
import { projectsManager } from '../../../services/projectsManager';
import { AIScriptService, TemplateCategory, ScriptTone, AIBatchScriptParams } from '../../../services/aiScriptService';
import { useNavigate } from 'react-router-dom';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import { UpgradePrompt } from '../../overlays/UpgradePrompt';
import './MakeItYoursWizard.scss';

const { TextArea } = Input;

interface MakeItYoursWizardProps {
  visible: boolean;
  onClose: () => void;
  onComplete?: (projectId: number) => void;
  onCreationStart?: () => void;
}

const STEP_LABELS = ['Details', 'Scenes'];

const GROUP_ORDER = ['basics', 'content', 'hook', 'demo', 'benefits', 'cta', 'other', 'delivery'];
const GROUP_LABELS: Record<string, string> = {
  basics: 'The Basics',
  content: 'Content Details',
  hook: 'Opening Hook',
  demo: 'Demo Details',
  benefits: 'Benefits & Value',
  cta: 'Call to Action',
  other: 'Additional Info',
  delivery: 'Delivery Style'
};
const GROUP_ICONS: Record<string, string> = {
  basics: '📝',
  content: '📚',
  hook: '🎯',
  demo: '🎬',
  benefits: '✨',
  cta: '🚀',
  other: 'ℹ️',
  delivery: '🎤'
};

export const MakeItYoursWizard: React.FC<MakeItYoursWizardProps> = ({
  visible,
  onClose,
  onComplete,
  onCreationStart
}) => {
  const [state, setState] = useAtom(makeItYoursStateAtom);
  const closeWizard = useSetAtom(closeWizardAtom);
  const navigate = useNavigate();
  const hasTriggeredAutoGenerate = useRef(false);
  const [currentSubStep, setCurrentSubStep] = useState(0);
  const [visitedGroups, setVisitedGroups] = useState<Set<string>>(new Set());
  
  // Premium feature gating for AI script generation
  const { isPaid } = useFeatureAccess();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  
  const hasCustomInputs = state?.inputDefinitions && state.inputDefinitions.length > 0;
  const inputDefinitions = state?.inputDefinitions || [];
  
  const activeGroups = useMemo(() => {
    if (!hasCustomInputs) return [];
    const groups: Record<string, typeof inputDefinitions> = {};
    inputDefinitions.forEach(input => {
      const group = input.group || 'other';
      if (!groups[group]) groups[group] = [];
      groups[group].push(input);
    });
    if (!groups['delivery']) groups['delivery'] = [];
    return GROUP_ORDER.filter(g => groups[g] && (groups[g].length > 0 || g === 'delivery'));
  }, [hasCustomInputs, inputDefinitions]);

  const handleClose = () => {
    hasTriggeredAutoGenerate.current = false;
    setCurrentSubStep(0);
    setVisitedGroups(new Set());
    closeWizard();
    onClose();
  };

  useEffect(() => {
    setCurrentSubStep(0);
    setVisitedGroups(new Set());
  }, [state?.baseTemplate?.id, state?.currentStep]);

  useEffect(() => {
    if (activeGroups.length > 0 && currentSubStep < activeGroups.length) {
      const currentGroup = activeGroups[currentSubStep];
      setVisitedGroups(prev => new Set([...Array.from(prev), currentGroup]));
    }
  }, [currentSubStep, activeGroups]);

  const updateState = useCallback((updates: Partial<typeof state>) => {
    if (state) {
      setState({ ...state, ...updates } as typeof state);
    }
  }, [state, setState]);

  const goToStep = (step: 1 | 2) => {
    updateState({ currentStep: step });
  };

  const handleNext = async () => {
    if (state && state.currentStep === 1) {
      if (hasCustomInputs && !state.topic.trim()) {
        const firstInput = inputDefinitions[0];
        const derivedTopic = firstInput ? state.dynamicInputs[firstInput.id] || '' : '';
        updateState({ topic: derivedTopic });
      }
      goToStep(2);
    }
  };

  const handleBack = () => {
    if (state && state.currentStep > 1) {
      goToStep(1);
    }
  };

  const handleComplete = async () => {
    if (!state || !state.baseTemplate) return;
    
    // Trigger loading overlay in parent
    onCreationStart?.();
    
    try {
      const projectName = state.topic || state.baseTemplate.title;
      const newProject = await projectsManager.createProject({
        name: projectName,
        description: `Created from ${state.baseTemplate.title} template`,
        aspectRatio: state.aspectRatio,
        templateId: state.baseTemplate.id,
      }, true);

      for (let i = 0; i < state.scenes.length; i++) {
        const scene = state.scenes[i];
        await projectsManager.createScene({
          projectId: newProject.id,
          title: scene.title,
          description: scene.script,
          videoUrl: undefined,
          duration: scene.duration * 1000,
          sceneOrder: i,
          metadata: {
            script: scene.script,
            layoutPreset: scene.layoutPreset,
            teleprompterEnabled: state.features.teleprompter,
            coachingEnabled: state.features.coaching
          }
        });
      }

      if (onComplete) {
        onComplete(newProject.id);
      } else {
        navigate(`/recorder?mode=project&projectId=${newProject.id}`);
      }
    } catch (error) {
      console.error('Failed to create project:', error);
      message.error('Failed to create project. Please try again.');
    }
  };

  const updateScene = (index: number, updates: Partial<CustomizedScene>) => {
    if (!state) return;
    const newScenes = [...state.scenes];
    newScenes[index] = { ...newScenes[index], ...updates };
    updateState({ scenes: newScenes });
  };

  const mapToneToScriptTone = (tone: ContentTone): ScriptTone => {
    switch (tone) {
      case 'casual': return 'casual';
      case 'inspiring': return 'energetic';
      case 'educational': return 'professional';
      case 'professional':
      default: return 'professional';
    }
  };

  const formatPlatformName = (platform: string | null): string => {
    if (!platform) return '';
    const names: Record<string, string> = {
      youtube: 'YouTube', linkedin: 'LinkedIn', instagram: 'Instagram',
      tiktok: 'TikTok', twitter: 'Twitter/X', course: 'Online Course',
      internal: 'Internal Training', multiple: 'Multiple Platforms'
    };
    return names[platform] || platform;
  };

  const formatAudienceName = (audience: string | null): string => {
    if (!audience) return '';
    const names: Record<string, string> = {
      customers: 'Customers', investors: 'Investors', students: 'Students',
      team: 'Team Members', public: 'General Public', developers: 'Developers'
    };
    return names[audience] || audience;
  };

  const formatGoalName = (goal: string | null): string => {
    if (!goal) return '';
    const names: Record<string, string> = {
      teach: 'Teach/Educate', demo: 'Product Demo', sell: 'Sell/Promote',
      introduce: 'Introduce/Announce', document: 'Document/Explain', testimonial: 'Testimonial/Case Study'
    };
    return names[goal] || goal;
  };

  const buildSceneContext = (sceneIndex: number, totalScenes: number): string => {
    const parts: string[] = [];
    parts.push(`Scene ${sceneIndex + 1} of ${totalScenes}`);
    parts.push(`Audience level: ${state?.audienceLevel || 'intermediate'}`);
    
    const ctx = state?.smartWizardContext;
    if (ctx?.platform) {
      parts.push(`Target platform: ${formatPlatformName(ctx.platform)}`);
    }
    if (ctx?.audience) {
      parts.push(`Target audience: ${formatAudienceName(ctx.audience)}`);
    }
    if (ctx?.goal) {
      parts.push(`Content goal: ${formatGoalName(ctx.goal)}`);
    }
    
    if (state?.keyPoints?.trim()) {
      parts.push(`Key points to cover: ${state.keyPoints}`);
    }
    if (state?.callToAction?.trim()) {
      parts.push(`Call to action: ${state.callToAction}`);
    }
    return parts.join('. ');
  };

  const retrySceneGeneration = async (index: number) => {
    // Gate AI generation for premium users only
    if (!isPaid) {
      setShowUpgradePrompt(true);
      return;
    }
    
    if (!state || !state.baseTemplate) return;
    
    const scene = state.scenes[index];
    updateScene(index, { aiStatus: 'generating' });

    try {
      const category = (state.baseTemplate.category || 'business') as TemplateCategory;
      const scriptTone = mapToneToScriptTone(state.tone);
      
      const ctx = state.smartWizardContext;
      // Build scene context including template script for adaptation
      let sceneContext = buildSceneContext(index, state.scenes.length);
      if (scene.templateScript) {
        sceneContext += `\n\nADAPT THIS TEMPLATE SCRIPT:\n"${scene.templateScript}"`;
      }
      if (scene.sceneNotes) {
        sceneContext += `\nScene notes: ${scene.sceneNotes}`;
      }
      
      const response = await AIScriptService.generateScript({
        templateType: state.baseTemplate.title,
        category,
        userTopic: state.topic,
        tone: scriptTone,
        duration: scene.duration,
        sceneTitle: scene.title,
        sceneContext,
        keyPoints: state.keyPoints || undefined,
        callToAction: state.callToAction || undefined,
        audienceLevel: state.audienceLevel,
        targetPlatform: ctx?.platform ? formatPlatformName(ctx.platform) : undefined,
        targetAudience: ctx?.audience ? formatAudienceName(ctx.audience) : undefined,
        contentGoal: ctx?.goal ? formatGoalName(ctx.goal) : undefined
      });

      updateScene(index, { 
        script: response.script,
        aiStatus: 'generated',
        isCustomized: true
      });
      message.success(`Scene ${index + 1} script generated!`);
    } catch (error) {
      console.error('Failed to generate script for scene:', error);
      updateScene(index, { aiStatus: 'error' });
      message.error(`Failed to generate script for Scene ${index + 1}`);
    }
  };

  const generateAllScripts = async () => {
    // Gate AI generation for premium users only
    if (!isPaid) {
      setShowUpgradePrompt(true);
      return;
    }
    
    if (!state || state.isGeneratingAll || !state.baseTemplate) return;
    
    const scenesWithGeneratingStatus = state.scenes.map(scene => ({
      ...scene,
      aiStatus: 'generating' as SceneAIStatus
    }));
    updateState({ isGeneratingAll: true, scenes: scenesWithGeneratingStatus });

    try {
      const category = (state.baseTemplate.category || 'business') as TemplateCategory;
      const scriptTone = mapToneToScriptTone(state.tone);
      const ctx = state.smartWizardContext;
      
      const buildCustomFields = (): Record<string, string> | undefined => {
        if (!state.dynamicInputs || Object.keys(state.dynamicInputs).length === 0) {
          return undefined;
        }
        const result: Record<string, string> = {};
        inputDefinitions.forEach(def => {
          const value = state.dynamicInputs[def.id];
          if (value && value.trim().length > 0) {
            result[def.aiVariable || def.id] = value;
          }
        });
        return Object.keys(result).length > 0 ? result : undefined;
      };
      
      const batchParams: AIBatchScriptParams = {
        templateType: state.baseTemplate.title,
        category,
        userTopic: state.topic,
        tone: scriptTone,
        scenes: state.scenes.map(scene => ({
          originalTitle: scene.title,
          duration: scene.duration,
          templateScript: scene.templateScript,  // Pass original template script for AI adaptation
          sceneNotes: scene.sceneNotes,          // Pass scene notes for context
          templateName: state.baseTemplate?.title
        })),
        keyPoints: state.keyPoints || undefined,
        callToAction: state.callToAction || undefined,
        audienceLevel: state.audienceLevel,
        targetPlatform: ctx?.platform ? formatPlatformName(ctx.platform) : undefined,
        targetAudience: ctx?.audience ? formatAudienceName(ctx.audience) : undefined,
        contentGoal: ctx?.goal ? formatGoalName(ctx.goal) : undefined,
        customFields: buildCustomFields()
      };

      // Use unified script generation for coherent, narrative-aware scripts
      const response = await AIScriptService.generateUnifiedScript(batchParams);
      
      let successCount = 0;
      let failCount = 0;
      
      // Handle per-scene success/failure status
      const updatedScenes = state.scenes.map((scene, idx) => {
        const result = response.scenes[idx];
        const isSuccess = result && result.success !== false && result.script && result.script.trim().length > 0;
        
        if (isSuccess) {
          successCount++;
          return {
            ...scene,
            title: result.title && result.title.trim() ? result.title : scene.title,
            script: result.script,
            aiStatus: 'generated' as SceneAIStatus,
            isCustomized: true
          };
        }
        failCount++;
        return { ...scene, aiStatus: 'error' as SceneAIStatus };
      });
      
      updateState({ 
        scenes: updatedScenes, 
        isGeneratingAll: false, 
        hasGeneratedOnce: true 
      });
      
      if (failCount === 0) {
        message.success('All scripts generated with narrative flow! Feel free to edit them.');
      } else if (successCount > 0) {
        message.warning(`${successCount} of ${state.scenes.length} scripts generated. Use Retry for failed scenes.`);
      } else {
        message.error('Failed to generate scripts. Please try again.');
      }
    } catch (error) {
      console.error('Failed to generate scripts:', error);
      const errorScenes = state.scenes.map(scene => ({
        ...scene,
        aiStatus: 'error' as SceneAIStatus
      }));
      updateState({ scenes: errorScenes, isGeneratingAll: false, hasGeneratedOnce: true });
      message.error('Failed to generate scripts. Please try again.');
    }
  };

  useEffect(() => {
    if (
      visible &&
      state?.currentStep === 2 && 
      !state.hasGeneratedOnce && 
      !state.isGeneratingAll &&
      !hasTriggeredAutoGenerate.current
    ) {
      const hasTopicOrCustomInputs = state.topic.trim().length > 0 || 
        Object.values(state.dynamicInputs || {}).some(v => v.trim().length > 0);
      
      if (hasTopicOrCustomInputs) {
        hasTriggeredAutoGenerate.current = true;
        generateAllScripts();
      }
    }
  }, [visible, state?.currentStep]);
  
  useEffect(() => {
    if (!visible) {
      hasTriggeredAutoGenerate.current = false;
    }
  }, [visible]);

  const canProceed = () => {
    if (!state) return false;
    switch (state.currentStep) {
      case 1: {
        if (hasCustomInputs) {
          const requiredInputs = inputDefinitions.filter(i => i.required);
          const allRequiredFilled = requiredInputs.every(
            input => state.dynamicInputs[input.id]?.trim().length > 0
          );
          return allRequiredFilled;
        }
        return state.topic.trim().length > 0;
      }
      case 2: return state.scenes.length > 0 && !state.isGeneratingAll;
      default: return false;
    }
  };

  const updateDynamicInput = (id: string, value: string) => {
    if (!state) return;
    updateState({
      dynamicInputs: { ...state.dynamicInputs, [id]: value }
    });
  };

  const renderDynamicInput = (input: typeof inputDefinitions[0]) => {
    if (!state) return null;
    const value = state.dynamicInputs[input.id] || '';
    
    const renderInputField = () => {
      switch (input.type) {
        case 'textarea':
          return (
            <TextArea
              placeholder={input.placeholder}
              value={value}
              onChange={(e) => updateDynamicInput(input.id, e.target.value)}
              autoSize={{ minRows: 2, maxRows: 4 }}
              maxLength={input.maxLength}
            />
          );
        case 'select':
          return (
            <Select
              placeholder={input.placeholder || 'Select an option'}
              value={value || undefined}
              onChange={(val) => updateDynamicInput(input.id, val)}
              style={{ width: '100%' }}
            >
              {input.options?.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          );
        default:
          return (
            <Input
              placeholder={input.placeholder}
              value={value}
              onChange={(e) => updateDynamicInput(input.id, e.target.value)}
              maxLength={input.maxLength}
            />
          );
      }
    };
    
    return (
      <div key={input.id} className={`wizard-input-group ${input.type === 'textarea' ? 'full' : ''}`}>
        <label className="input-label">
          {input.label}
          {!input.required && <span className="optional-tag">Optional</span>}
        </label>
        {renderInputField()}
        {input.helpText && <span className="input-hint">{input.helpText}</span>}
      </div>
    );
  };

  const getInputsForGroup = (groupName: string) => {
    return inputDefinitions.filter(input => (input.group || 'other') === groupName);
  };

  const isGroupOptional = (groupName: string): boolean => {
    if (groupName === 'delivery') return true;
    const groupInputs = getInputsForGroup(groupName);
    return groupInputs.every(i => !i.required);
  };

  const isSubStepComplete = (groupName: string): boolean => {
    if (groupName === 'delivery') return visitedGroups.has(groupName);
    const groupInputs = getInputsForGroup(groupName);
    const requiredInputs = groupInputs.filter(i => i.required);
    if (requiredInputs.length === 0) return visitedGroups.has(groupName);
    return requiredInputs.every(i => {
      const val = state?.dynamicInputs[i.id];
      return val && val.trim().length > 0;
    });
  };

  const handleSubStepNext = () => {
    if (currentSubStep < activeGroups.length - 1) {
      setCurrentSubStep(currentSubStep + 1);
    }
  };

  const handleSubStepBack = () => {
    if (currentSubStep > 0) {
      setCurrentSubStep(currentSubStep - 1);
    }
  };

  const canAdvanceSubStep = (): boolean => {
    if (!hasCustomInputs || activeGroups.length === 0) return true;
    const currentGroup = activeGroups[currentSubStep];
    return isSubStepComplete(currentGroup);
  };

  const renderAccordionItem = (group: string, idx: number) => {
    const isExpanded = idx === currentSubStep;
    const isComplete = visitedGroups.has(group) && isSubStepComplete(group);
    const isOptional = isGroupOptional(group);
    const groupInputs = getInputsForGroup(group);
    const isLastGroup = idx === activeGroups.length - 1;

    return (
      <div 
        key={group} 
        className={`accordion-item ${isExpanded ? 'expanded' : ''} ${isComplete ? 'completed' : ''}`}
      >
        <button
          type="button"
          className="accordion-header"
          onClick={() => setCurrentSubStep(idx)}
        >
          <div className="accordion-header-left">
            <span className={`accordion-step-number ${isComplete ? 'done' : ''}`}>
              {isComplete ? <Check size={14} /> : idx + 1}
            </span>
            <span className="accordion-icon">{GROUP_ICONS[group]}</span>
            <span className="accordion-title">{GROUP_LABELS[group]}</span>
            {isOptional && <span className="accordion-optional">Optional</span>}
          </div>
          <ChevronRight size={18} className={`accordion-chevron ${isExpanded ? 'rotated' : ''}`} />
        </button>
        
        {isExpanded && (
          <div className="accordion-content">
            <div className="accordion-inputs">
              {group === 'delivery' ? (
                <>
                  {groupInputs.map(renderDynamicInput)}
                  <div className="wizard-input-group">
                    <label className="input-label">
                      <Mic size={16} className="label-icon" />
                      Tone
                    </label>
                    <div className="wizard-pill-group">
                      {[
                        { value: 'professional', label: 'Professional' },
                        { value: 'casual', label: 'Casual' },
                        { value: 'inspiring', label: 'Inspiring' },
                        { value: 'educational', label: 'Educational' }
                      ].map(opt => (
                        <button 
                          key={opt.value}
                          type="button"
                          className={`wizard-pill ${state?.tone === opt.value ? 'selected' : ''}`}
                          onClick={() => updateState({ tone: opt.value as ContentTone })}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                groupInputs.map(renderDynamicInput)
              )}
            </div>
            
            {!isLastGroup && (
              <button
                type="button"
                className={`accordion-next-btn ${!canAdvanceSubStep() ? 'disabled' : ''}`}
                onClick={handleSubStepNext}
                disabled={!canAdvanceSubStep()}
              >
                Continue
                <ChevronRight size={16} />
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderAccordion = () => {
    if (!hasCustomInputs || activeGroups.length === 0) return null;
    
    return (
      <div className="accordion-container">
        {activeGroups.map((group, idx) => renderAccordionItem(group, idx))}
      </div>
    );
  };

  const renderStep1 = () => (
    <div className="wizard-step step-topic">
      <h3 className="wizard-step-title">Tell Us About Your Recording</h3>
      <p className="wizard-step-subtitle">
        {hasCustomInputs 
          ? `Fill in the details for your ${state?.baseTemplate?.title || 'video'}`
          : 'The more details you share, the better your AI-generated scripts will be'}
      </p>
      
      {hasCustomInputs ? (
        renderAccordion()
      ) : (
        <>
          <div className="wizard-input-group">
            <label className="input-label">
              <Sparkles size={16} className="label-icon" />
              Topic or Title
            </label>
            <Input
              placeholder="e.g., How to use our new CRM dashboard"
              value={state?.topic || ''}
              onChange={(e) => updateState({ topic: e.target.value })}
              maxLength={150}
              size="large"
            />
            <span className="input-hint">This will be your project name</span>
          </div>

          <div className="wizard-row-inputs">
            <div className="wizard-input-group half">
              <label className="input-label">
                <Users size={16} className="label-icon" />
                Audience Level
              </label>
              <div className="wizard-pill-group">
                {[
                  { value: 'beginner', label: 'Beginner' },
                  { value: 'intermediate', label: 'Intermediate' },
                  { value: 'advanced', label: 'Advanced' }
                ].map(opt => (
                  <button 
                    key={opt.value}
                    type="button"
                    className={`wizard-pill ${state?.audienceLevel === opt.value ? 'selected' : ''}`}
                    onClick={() => updateState({ audienceLevel: opt.value as 'beginner' | 'intermediate' | 'advanced' })}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="wizard-input-group half">
              <label className="input-label">
                <Mic size={16} className="label-icon" />
                Tone
              </label>
              <div className="wizard-pill-group">
                {[
                  { value: 'professional', label: 'Professional' },
                  { value: 'casual', label: 'Casual' },
                  { value: 'inspiring', label: 'Inspiring' },
                  { value: 'educational', label: 'Educational' }
                ].map(opt => (
                  <button 
                    key={opt.value}
                    type="button"
                    className={`wizard-pill ${state?.tone === opt.value ? 'selected' : ''}`}
                    onClick={() => updateState({ tone: opt.value as ContentTone })}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <div className="wizard-input-group">
            <label className="input-label">
              <MessageSquare size={16} className="label-icon" />
              Key Points to Cover
              <span className="optional-tag">Optional</span>
            </label>
            <TextArea
              placeholder="e.g., Main features, benefits, common use cases..."
              value={state?.keyPoints || ''}
              onChange={(e) => updateState({ keyPoints: e.target.value })}
              autoSize={{ minRows: 2, maxRows: 4 }}
              maxLength={500}
            />
            <span className="input-hint">What topics or features should be mentioned?</span>
          </div>

          <div className="wizard-input-group">
            <label className="input-label">
              <Target size={16} className="label-icon" />
              Call to Action
              <span className="optional-tag">Optional</span>
            </label>
            <Input
              placeholder="e.g., Sign up for a free trial, Schedule a demo..."
              value={state?.callToAction || ''}
              onChange={(e) => updateState({ callToAction: e.target.value })}
              maxLength={150}
            />
            <span className="input-hint">What should viewers do after watching?</span>
          </div>
        </>
      )}
      
      <div className="template-preview-card">
        <div className="preview-header">
          <span className="preview-label">Based on template:</span>
          <span className="template-name">{state?.baseTemplate?.title}</span>
        </div>
        <div className="preview-meta">
          <span><Clock size={14} /> {state?.scenes?.length || 0} scenes</span>
          <span><Layout size={14} /> {state?.aspectRatio}</span>
        </div>
      </div>
    </div>
  );

  const renderSceneCard = (scene: CustomizedScene, index: number) => {
    const isGenerating = scene.aiStatus === 'generating';
    const isGenerated = scene.aiStatus === 'generated';
    const hasError = scene.aiStatus === 'error';

    return (
      <div key={index} className={`scene-editor-card ${isGenerating ? 'generating' : ''} ${hasError ? 'has-error' : ''}`}>
        <div className="scene-header">
          <span className="scene-number">Scene {index + 1}</span>
          <div className="scene-header-right">
            <span className="scene-duration">{scene.duration}s</span>
            {isGenerated && (
              <Tooltip title="AI Generated">
                <span className="ai-badge">
                  <Sparkles size={12} />
                </span>
              </Tooltip>
            )}
          </div>
        </div>
        
        <Input
          value={scene.title}
          onChange={(e) => updateScene(index, { title: e.target.value, isCustomized: true })}
          placeholder="Scene title"
          className="scene-title-input"
          disabled={isGenerating}
        />
        
        <div className="script-area-wrapper">
          {isGenerating ? (
            <div className="script-generating-placeholder">
              <Loader2 size={20} className="spin-animation" />
              <span>Writing script...</span>
            </div>
          ) : (
            <TextArea
              value={scene.script}
              onChange={(e) => updateScene(index, { script: e.target.value, isCustomized: true })}
              placeholder="Your script will appear here..."
              autoSize={{ minRows: 3, maxRows: 6 }}
              className="scene-script-input"
            />
          )}
        </div>

        {hasError && (
          <div className="scene-error-row">
            <span className="error-text">Script generation failed</span>
            <Button 
              type="text" 
              size="small" 
              icon={<RefreshCw size={12} />}
              onClick={() => retrySceneGeneration(index)}
              className="retry-btn"
            >
              Retry
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderStep2 = () => (
    <div className="wizard-step step-scenes">
      <div className="step-header-simple">
        <h3 className="wizard-step-title">Your AI-Generated Scripts</h3>
        <p className="wizard-step-subtitle">
          {state?.isGeneratingAll 
            ? 'AI is writing personalized scripts based on your inputs...' 
            : 'Review and edit the scripts below, then create your project'}
        </p>
      </div>
      
      <div className="scenes-list">
        {state?.scenes.map((scene, index) => renderSceneCard(scene, index))}
      </div>
    </div>
  );

  if (!state) return null;

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={640}
      className="wizard-modal make-it-yours-modal"
      destroyOnHidden
      title={
        <div className="wizard-header">
          <Sparkles size={20} className="header-icon" />
          Make It Yours
        </div>
      }
    >
      <WizardProgress 
        currentStep={state.currentStep} 
        totalSteps={2}
        stepLabels={STEP_LABELS}
      />
      
      <div className="wizard-content">
        {state.currentStep === 1 && renderStep1()}
        {state.currentStep === 2 && renderStep2()}
      </div>
      
      <WizardNavigation
        currentStep={state.currentStep}
        totalSteps={2}
        onBack={handleBack}
        onNext={handleNext}
        onComplete={handleComplete}
        canProceed={canProceed()}
        isLoading={false}
        completeLabel="Create Project"
        nextLabel={state.currentStep === 1 ? "Generate Scripts" : "Next"}
      />
      
      <UpgradePrompt
        visible={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        feature="AI Script Generation"
        description="Upgrade to Pro to unlock AI-powered script generation. Create professional scripts for all your scenes in seconds with the help of artificial intelligence."
      />
    </Modal>
  );
};

export default MakeItYoursWizard;
